import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { 
  CheckIcon,
  BoltIcon,
  ShieldCheckIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline'
import LoginForm from './LoginForm'
import RegisterForm from './RegisterForm'
import ForgotPasswordForm from './ForgotPasswordForm'
import CookieConsentBanner from './CookieConsentBanner'
import { hasConsentBeenGiven } from '../../utils/cookieConsent'
import { useAuth } from '../../context/AuthContext'
import textBanner from '../../assets/images/text-banner-black-min.png'
import { getPricingPlans } from '../../config/plans'
import { trackAuthFormSwitch, trackBillingPeriodToggle, trackCTAClick } from '../../config/analyticsEvents'

const slideVariants = {
  enter: { x: 50, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exit: { x: -50, opacity: 0 }
}

const Auth = ({ onSuccess }) => {
  const [searchParams] = useSearchParams()
  const [currentForm, setCurrentForm] = useState('login')
  const [currentPlanIndex, setCurrentPlanIndex] = useState(0)
  const [isAutoRotating, setIsAutoRotating] = useState(true)
  const [showCookieBanner, setShowCookieBanner] = useState(false)
  const [showPricingPanel, setShowPricingPanel] = useState(true)
  const [isYearly, setIsYearly] = useState(true)
  const navigate = useNavigate()
  const { user, loading } = useAuth()

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, loading, navigate])

  // Check URL params for plan
  useEffect(() => {
    const plan = searchParams.get('plan')
    
    // Show pricing panel only when plan param is present
    if (plan === 'pro') {
      setShowPricingPanel(true)
      setCurrentPlanIndex(1) // Pro plan index
      setIsAutoRotating(false)
    } else if (plan === 'free') {
      setShowPricingPanel(true)
      setCurrentPlanIndex(0) // Free plan index
      setIsAutoRotating(true) // Auto-rotate starting from free
    } else {
      // Default: no plan param, show auth forms only
      setShowPricingPanel(false)
      setIsAutoRotating(false)
    }
  }, [searchParams])

  // Check if cookie consent banner should be shown
  useEffect(() => {
    setShowCookieBanner(!hasConsentBeenGiven())
  }, [])

  const handleAuthSuccess = (formType, email) => {
    if (formType === 'register' && email) {
      // After registration, redirect to email verification page
      navigate('/verify-email', { state: { email } })
    } else {
      // For login, redirect to dashboard
      navigate('/dashboard')
    }
    // Call the original onSuccess if provided
    onSuccess?.()
  }

  // Auto-rotate pricing plans every 5 seconds
  useEffect(() => {
    if (!isAutoRotating) return

    const interval = setInterval(() => {
      setCurrentPlanIndex((prev) => (prev + 1) % pricingPlans.length)
    }, 5000) // Change plan every 5 seconds

    return () => clearInterval(interval)
  }, [isAutoRotating])


  // Pause auto-rotation when user hovers over pricing card
  const handlePricingCardHover = (hovering) => {
    setIsAutoRotating(!hovering)
  }

  // Get pricing plans from centralized config
  const pricingPlans = getPricingPlans(isYearly).map(plan => ({
    ...plan,
    color: plan.id === 'free' ? 'gray' : 'blue',
    onSale: plan.popular
  }))

  const handleUpgradeClick = () => {
    setIsAutoRotating(false)
    setCurrentPlanIndex((prev) => (prev + 1) % pricingPlans.length)
    trackCTAClick('see_plan_benefits', 'auth_page')

    setTimeout(() => {
      setIsAutoRotating(true)
    }, 10000)
  }

  const forms = {
    login: LoginForm,
    register: RegisterForm,
    forgot: ForgotPasswordForm
  }

  const CurrentForm = forms[currentForm]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">

      <div className="flex h-screen">
        {/* Left Side - Pricing Plans */}
        {showPricingPanel && (
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 flex-col h-full overflow-y-auto">
          <div className="max-w-md mx-auto w-full p-12 flex flex-col justify-center min-h-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-8"
            >
              <h1 className="text-4xl font-bold text-white mb-4">
                Perfect App Icons, Instantly
              </h1>
              <p className="text-slate-300 text-lg mb-6">
                Generate stunning iOS and Android icons with our API. Start free with 10 calls.
              </p>
              
              {/* Billing Toggle */}
              <div className="flex items-center justify-center gap-3">
                <span className={`text-sm font-medium ${!isYearly ? 'text-white' : 'text-slate-400'}`}>
                  Monthly
                </span>
                <button
                  onClick={() => { setIsYearly(!isYearly); trackBillingPeriodToggle(!isYearly ? 'yearly' : 'monthly') }}
                  className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                    isYearly ? 'bg-primary-500' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-300 ${
                      isYearly ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className={`text-sm font-medium ${isYearly ? 'text-white' : 'text-slate-400'}`}>
                  Yearly
                </span>
                {isYearly && (
                  <span className="ml-1 px-2 py-0.5 text-xs font-semibold text-emerald-400 bg-emerald-500/20 rounded-full">
                    Save 40%
                  </span>
                )}
              </div>
            </motion.div>

            {/* Pricing Card Carousel */}
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentPlanIndex}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="relative"
                  onMouseEnter={() => handlePricingCardHover(true)}
                  onMouseLeave={() => handlePricingCardHover(false)}
                >
                  {/* Gradient background for Pro plan */}
                  {pricingPlans[currentPlanIndex].popular && (
                    <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 via-emerald-500 to-primary-500 rounded-2xl blur opacity-40 group-hover:opacity-60 transition duration-300 animate-pulse"></div>
                  )}
                  
                  <div className={`relative overflow-hidden rounded-2xl p-8 ${
                    pricingPlans[currentPlanIndex].popular 
                      ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 shadow-2xl' 
                      : 'bg-white/10 backdrop-blur-sm border border-white/20'
                  }`}>
                  {/* Sale Background Effect */}
                  {pricingPlans[currentPlanIndex].onSale && (
                    <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 via-pink-500/5 to-yellow-500/10 animate-pulse"></div>
                  )}
                  <div className="relative text-center mb-6 z-10">
                    <div className="flex items-center justify-center mb-3">
                      <h3 className="text-3xl font-bold text-white drop-shadow-lg">
                        {pricingPlans[currentPlanIndex].name}
                      </h3>
                      {pricingPlans[currentPlanIndex].onSale && (
                        <div className="relative ml-3">
                          <span className="relative inline-flex items-center px-3 py-1 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-full shadow-lg">
                            <span className="relative flex items-center">
                              SALE
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      {pricingPlans[currentPlanIndex].onSale ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-center space-x-4">
                            <span className="text-2xl font-light text-gray-400 line-through opacity-60">
                              {pricingPlans[currentPlanIndex].originalPrice}
                            </span>
                            <span className="text-5xl font-bold text-white drop-shadow-xl">
                              {pricingPlans[currentPlanIndex].price}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-5xl font-bold text-white drop-shadow-lg">
                          {pricingPlans[currentPlanIndex].price}
                        </span>
                      )}
                      <div className="text-slate-300 text-lg font-medium mt-1">
                        {pricingPlans[currentPlanIndex].period}
                      </div>
                      {pricingPlans[currentPlanIndex].periodNote && (
                        <p className="text-emerald-400 text-sm font-semibold mt-2">
                          {pricingPlans[currentPlanIndex].periodNote}
                        </p>
                      )}
                    </div>
                    {pricingPlans[currentPlanIndex].onSale && !pricingPlans[currentPlanIndex].periodNote && (
                      <p className="text-emerald-400 text-sm font-bold mb-3">
                        💰 Save 50% - Limited Time!
                      </p>
                    )}
                    <p className="text-slate-300 text-sm">
                      {pricingPlans[currentPlanIndex].description}
                    </p>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {pricingPlans[currentPlanIndex].features.map((feature, index) => (
                      <li key={index} className="flex items-center text-slate-300">
                        <CheckIcon className="h-5 w-5 text-emerald-400 mr-3 flex-shrink-0" />
                        <span className="text-sm flex-1">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Plan Indicators */}
              <div className="flex justify-center space-x-2 mt-4">
                {pricingPlans.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentPlanIndex(index)
                      setIsAutoRotating(false)
                      setTimeout(() => setIsAutoRotating(true), 10000)
                    }}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentPlanIndex 
                        ? 'bg-white scale-125' 
                        : 'bg-white/40 hover:bg-white/60'
                    }`}
                  />
                ))}
              </div>

              {/* Upgrade CTA */}
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={handleUpgradeClick}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold shadow-soft hover:shadow-hover transition ${
                    pricingPlans[currentPlanIndex].name === 'Free'
                      ? 'text-white api-cta-gradient hover:opacity-95' 
                      : 'text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  <RocketLaunchIcon className="h-5 w-5" />
                  {pricingPlans[currentPlanIndex].name === 'Free' ? 'See Pro Benefits' : 'View Free Plan'}
                </button>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Right Side - Auth Forms */}
        <div className={`w-full ${showPricingPanel ? 'lg:w-1/2' : ''} flex flex-col h-full overflow-y-auto`}>
          <div className="flex-1 p-8">
            <div className="w-full max-w-md mx-auto min-h-full flex flex-col justify-center">
            {/* Brand Banner */}
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center hover:opacity-80 transition-opacity">
                <div className="relative">
                  <img 
                    src={textBanner} 
                    alt="EasyAppIcon API" 
                    className="h-10"
                  />
                  <span className="absolute -top-1 -right-8 bg-[#df6e57] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    API
                  </span>
                </div>
              </Link>
            </div>
            
            {/* Mobile Pricing Summary */}
            {showPricingPanel && (
            <div className="lg:hidden mb-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 rounded-2xl"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-white/10 rounded-2xl"></div>
              <div className="relative p-6 text-white">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl font-bold">Developer-First API</h2>
                  <div className="relative">
                    <span className="relative inline-flex items-center px-3 py-1 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-full shadow-lg">
                      <span className="relative">SALE</span>
                    </span>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-3">Start free with 10 monthly calls</p>
                
                {/* Mobile Billing Toggle */}
                <div className="flex items-center justify-center gap-3 mb-4">
                  <span className={`text-xs font-medium ${!isYearly ? 'text-white' : 'text-slate-400'}`}>
                    Monthly
                  </span>
                  <button
                    onClick={() => { setIsYearly(!isYearly); trackBillingPeriodToggle(!isYearly ? 'yearly' : 'monthly') }}
                    className={`relative w-10 h-5 rounded-full transition-colors duration-300 ${
                      isYearly ? 'bg-primary-500' : 'bg-slate-600'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-300 ${
                        isYearly ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className={`text-xs font-medium ${isYearly ? 'text-white' : 'text-slate-400'}`}>
                    Yearly
                  </span>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-4 border border-emerald-400/30">
                  <div className="flex items-center justify-center space-x-4 mb-2">
                    <span className="text-base font-light text-gray-400 line-through opacity-60">
                      {isYearly ? '$120/year' : '$10/month'}
                    </span>
                    <span className="text-3xl font-bold text-white">
                      {isYearly ? '$36/year' : '$5/month'}
                    </span>
                  </div>
                  {isYearly && (
                    <p className="text-emerald-400 text-sm font-semibold text-center">
                      $3/month · Save $24 per year
                    </p>
                  )}
                </div>
                <div className="flex space-x-4 text-xs">
                  <div className="flex items-center">
                    <BoltIcon className="h-4 w-4 mr-1" />
                    <span>Instant</span>
                  </div>
                  <div className="flex items-center">
                    <ShieldCheckIcon className="h-4 w-4 mr-1" />
                    <span>Reliable</span>
                  </div>
                </div>
              </div>
            </div>
            )}

            <AnimatePresence mode="wait" custom={currentForm}>
              <motion.div
                key={currentForm}
                custom={currentForm}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
              >
                <CurrentForm 
                  onToggleForm={(form) => { setCurrentForm(form); trackAuthFormSwitch(form) }} 
                  onSuccess={(email) => handleAuthSuccess(currentForm, email)}
                  hasPlan={showPricingPanel}
                />
              </motion.div>
            </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Consent Banner - Only shown on Auth page when consent not given */}
      {showCookieBanner && (
        <CookieConsentBanner onConsentGiven={() => setShowCookieBanner(false)} />
      )}
    </div>
  )
}

export default Auth
